/*
 * Copyright (c) 2026. Bluemate B.V.
 */
import IDate from '@dvrd/idate';

import {StatusTileState} from '../components/controls/statusTile/bluemateStatusTile';

import {AccountProgress, AccountStatus} from './interfaces';

/**
 * How the account's API status reads on the overview's connection tile. Same
 * reading as the stats card on the old dashboard (`getApiIcons`): idle and
 * importing both mean the link with bol stands — importing only says the first
 * data pull is still running. Before the account is loaded nothing is known yet,
 * which is not the same as being disconnected. Anything else is a connection the
 * seller does not have — never finished or broken — and both read as a fault.
 */
export function bolConnectionState(status: AccountStatus | undefined | null): StatusTileState {
    switch (status) {
        case AccountStatus.IDLE:
        case AccountStatus.IMPORTING:
            return 'green';
        case AccountStatus.SETUP_API:
        case AccountStatus.ERROR:
            return 'red';
        default:
            return 'gray';
    }
}

/**
 * Auto e-mailing and auto upload are preferences, not obligations: a seller may
 * decide never to switch them on. So "off" is gray — a plain read-out — and
 * never red or orange, which the banner reserves for something being wrong.
 */
export function featureToggleState(enabled: boolean | undefined | null): StatusTileState {
    return enabled ? 'green' : 'gray';
}

/** How long a fresh bol connection keeps the setup steps on screen. */
const SETUP_GRACE_DAYS = 7;

/**
 * Whether the setup steps still have something to say — the same rule the old
 * dashboard's to-do list uses.
 *
 * Two things have to hold. A step must still be open, and the account must be
 * new enough for the reminder to be welcome: without a bol connection the seller
 * is still setting up, and for the first week after connecting the list keeps
 * nudging. After that it goes away for good, even with steps left unticked —
 * someone who has run the account for a week and never designed an invoice has
 * decided, not forgotten.
 */
export function shouldShowSetupSteps(
    progress: AccountProgress | undefined | null,
    apiKeyActiveSince: IDate | null
): boolean {
    if (!progress) return false;
    const stepOpen = !progress.pdf_layout || !progress.vat || !progress.mail || !progress.upload;
    if (!stepOpen) return false;
    if (!apiKeyActiveSince) return true;
    return apiKeyActiveSince.isAtOrAfter(IDate.now().subtract(SETUP_GRACE_DAYS, 'days'));
}
